import type { Diagnostics, Factory, Source } from '@servicenow/sdk-build-core'
import {
    CallExpressionShape,
    IdentifierShape,
    PropertyAccessShape,
    type RecordId,
    type Shape,
    StringLiteralShape,
    StringShape,
    TemplateExpressionShape,
} from '@servicenow/sdk-build-core'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import {
    ACTIVITY_CALLEE_NAMES,
    ACTIVITY_TABLE,
    CURRENT_ACTIVITY_CALLEE,
    DATAPILL_CALLEE,
    LANE_CALLEE_NAMES,
    TABLE_NAME_SUFFIX,
} from '../constants/playbook-plugin-constants'
import { extractIdFromCallExpression } from '../transforms/reference-resolver'
import { parseArrowBodyEntries } from './arrow-body-parser'

/** Returns TABLE_NAME_SUFFIX when isTableName is true, otherwise empty string. */
const tnSuffix = (isTableName: boolean) => (isTableName ? TABLE_NAME_SUFFIX : '')

// ============================================================================
// DeferredPillShape — wraps a RecordId for lazy GUID resolution in pill strings
// ============================================================================

/**
 * A StringShape whose getValue() lazily constructs a pill string at XML serialization time.
 *
 * Used for activity-output pills where the referenced activity's GUID may not yet
 * be known when the pill is created (deferred via RecordId).
 *
 * Standard format:  `{{act.<sysId>.<path>}}`
 * Condition format: `activity_<sysId>.<path>`
 */
export class DeferredPillShape extends StringShape {
    private readonly ref: RecordId
    private readonly pillPath: string
    private readonly isCondition: boolean
    private readonly isTableName: boolean

    constructor({
        source,
        ref,
        pillPath,
        isCondition = false,
        isTableName = false,
    }: {
        source: Source
        ref: RecordId
        pillPath: string
        isCondition?: boolean
        isTableName?: boolean
    }) {
        super({ source })
        this.ref = ref
        this.pillPath = pillPath
        this.isCondition = isCondition
        this.isTableName = isTableName
    }

    override getValue(): string {
        const sysId = this.ref.getValue()
        if (this.isCondition) {
            // _tableName is not allowed in conditions (blocked earlier with a diagnostic error)
            return `activity_${sysId}.${this.pillPath}`
        }
        return `{{act.${sysId}.${this.pillPath}${tnSuffix(this.isTableName)}}}`
    }
}

// ============================================================================
// DeferredTemplatePillShape — deferred pill string embedded in a template
// ============================================================================

/**
 * A StringShape whose getValue() concatenates static text + deferred pill parts.
 * Used when a template literal mixes plain text with pill references that
 * contain deferred RecordId GUIDs.
 */
export class DeferredTemplatePillShape extends StringShape {
    private readonly parts: Array<string | DeferredPillShape | DeferredTemplatePillShape>

    constructor({
        source,
        parts,
    }: { source: Source; parts: Array<string | DeferredPillShape | DeferredTemplatePillShape> }) {
        super({ source })
        this.parts = parts
    }

    override getValue(): string {
        return this.parts.map((p) => (typeof p === 'string' ? p : p.getValue())).join('')
    }
}

// ============================================================================
// Pill format and context types
// ============================================================================

export type PillFormat = 'standard' | 'condition'

export interface PillContext {
    format: PillFormat
    factory: Factory
    diagnostics: Diagnostics
    /** RecordId of the current activity — for self-referencing label/description pills */
    currentActivityId?: RecordId
    /**
     * Names of arrow function parameters that represent PlaybookBodyParams,
     * collected from all enclosing scopes (lanes callback, activities callback).
     * Used to detect `<param>.inputs.*` and `<param>.state` pill references
     * regardless of what the user named the parameter.
     */
    playbookBodyParamsNames: string[]
    /**
     * Name of the trigger parameter in trigger mapper callbacks (e.g., 'trigger').
     * Used to detect `<param>.current.*` pill references in trigger input mappings.
     */
    triggerParamName?: string
    /** Which pill sources are allowed in this context */
    allowedSources: {
        paramsInputs: boolean
        paramsState: boolean
        paramsParentRecord: boolean
        previousActivityOutputs: boolean
        currentActivityLabelDesc: boolean
        automationPlan: boolean
    }
}

// ============================================================================
// Pill resolution helpers
// ============================================================================

function isDataPillCall(shape: Shape): shape is CallExpressionShape {
    return shape instanceof CallExpressionShape && shape.getCallee() === DATAPILL_CALLEE
}

function extractPillReference(dataPillCall: CallExpressionShape): Shape | undefined {
    return dataPillCall.hasArgument(0) ? dataPillCall.getArgument(0, false) : undefined
}

function buildPath(elements: readonly IdentifierShape[], fromIndex: number): string {
    return elements
        .slice(fromIndex)
        .map((el) => el.getName())
        .join('.')
}

/**
 * Resolve a PropertyAccessShape rooted at a PlaybookBodyParams parameter to a pill string.
 *
 * - <param>.inputs.x.y      -> standard: `{{pd.inputs.x.y}}`      / condition: `inputs.x.y`
 * - <param>.state            -> standard: `{{pd.state}}`
 * - <param>.parentRecord.x.y -> standard: `{{pd.inputRecord.x.y}}` / condition: `input_record.x.y`
 */
function resolveParamsPill(
    elements: readonly IdentifierShape[],
    format: PillFormat,
    shape: Shape,
    diagnostics: Diagnostics,
    allowedSources: PillContext['allowedSources'],
    isTableName: boolean
): string | undefined {
    const second = elements[1]?.getName()

    if (second === 'inputs') {
        if (!allowedSources.paramsInputs) {
            diagnostics.error(shape, 'params.inputs pills are not allowed in this context')
            return undefined
        }
        const path = buildPath(elements, 2)
        if (!path) {
            diagnostics.error(shape, 'params.inputs must be followed by a field path (e.g., params.inputs.varName)')
            return undefined
        }
        return format === 'standard' ? `{{pd.inputs.${path}${tnSuffix(isTableName)}}}` : `inputs.${path}`
    }

    if (second === 'state') {
        if (!allowedSources.paramsState || format === 'condition') {
            diagnostics.error(shape, 'params.state is not allowed in this context')
            return undefined
        }
        if (elements.length > 2) {
            diagnostics.error(shape, 'params.state is terminal, additional property access is not allowed')
            return undefined
        }
        return '{{pd.state}}'
    }

    if (second === 'parentRecord') {
        if (!allowedSources.paramsParentRecord) {
            diagnostics.error(shape, 'params.parentRecord pills are not allowed in this context')
            return undefined
        }
        const path = buildPath(elements, 2)
        if (path) {
            return format === 'standard' ? `{{pd.inputRecord.${path}${tnSuffix(isTableName)}}}` : `input_record.${path}`
        }
        return format === 'standard' ? `{{pd.inputRecord${tnSuffix(isTableName)}}}` : 'input_record'
    }

    return undefined
}

/**
 * Given a Lane CallExpressionShape and an activity name, find the Activity call
 * within that lane's activities arrow function.
 *
 * Traverses: Lane({ activities: (params) => ({ act1: Activity(...) }) })
 *            → finds and returns the Activity(...) CallExpressionShape for `act1`
 */
function findActivityInLane(laneCallExpr: CallExpressionShape, activityName: string): CallExpressionShape | undefined {
    const laneDef = laneCallExpr.getArgument(0)?.ifObject()
    const activitiesShape = laneDef?.get('activities')
    if (!activitiesShape?.is(ArrowFunctionShape)) {
        return undefined
    }

    const { variables } = parseArrowBodyEntries(activitiesShape.as(ArrowFunctionShape))
    const activityCallExpr = variables.get(activityName)
    if (activityCallExpr && ACTIVITY_CALLEE_NAMES.has(activityCallExpr.getCallee())) {
        return activityCallExpr
    }
    return undefined
}

/**
 * Resolve a PropertyAccessShape rooted at an activity or lane variable to a pill shape.
 *
 * Handles two patterns:
 * - Same-lane:  `act1.outputs.field`        — root resolves to Activity()
 * - Cross-lane: `lane1.act1.outputs.field`  — root resolves to Lane(), second element is the activity name
 *
 * The $id is extracted via `extractIdFromCallExpression` and a DeferredPillShape is returned.
 */
async function resolveActivityPill(
    refShape: PropertyAccessShape,
    elements: readonly IdentifierShape[],
    format: PillFormat,
    factory: Factory,
    diagnostics: Diagnostics,
    allowedSources: PillContext['allowedSources'],
    isTableName: boolean
): Promise<Shape | undefined> {
    const root = elements[0]
    if (!root) {
        return undefined
    }

    let resolved: Shape
    try {
        resolved = root.resolve(true)
    } catch {
        return undefined
    }

    if (!(resolved instanceof CallExpressionShape)) {
        return undefined
    }

    const callee = resolved.getCallee()

    let activityCallExpr: CallExpressionShape | undefined
    let pathStartIndex: number

    if (ACTIVITY_CALLEE_NAMES.has(callee)) {
        // Same-lane: act1.outputs.field
        activityCallExpr = resolved as CallExpressionShape
        pathStartIndex = 1
    } else if (LANE_CALLEE_NAMES.has(callee)) {
        // Cross-lane: lane1.act1.outputs.field
        if (elements.length < 3) {
            diagnostics.error(
                refShape,
                'Cross-lane data pill must include an activity name and property path (e.g., lane1.act1.outputs.field)'
            )
            return undefined
        }
        const activityName = elements[1]?.getName()
        if (!activityName) {
            return undefined
        }
        activityCallExpr = findActivityInLane(resolved as CallExpressionShape, activityName)
        if (!activityCallExpr) {
            diagnostics.error(
                refShape,
                `Cannot find activity "${activityName}" in lane for cross-lane data pill reference`
            )
            return undefined
        }
        pathStartIndex = 2
    } else {
        return undefined
    }

    const idShape = extractIdFromCallExpression(activityCallExpr)
    if (!idShape) {
        diagnostics.error(refShape, 'Cannot resolve activity $id for data pill reference')
        return undefined
    }
    const explicitId = String(idShape.getValue())

    const pillPath = buildPath(elements, pathStartIndex)

    if (pillPath.startsWith('sysId.') || pillPath.startsWith('state.')) {
        diagnostics.error(refShape, `Activity ${pillPath} cannot be used as a pill in this context`)
        return undefined
    }

    if (pillPath === 'sysId') {
        if (!allowedSources.previousActivityOutputs) {
            diagnostics.error(refShape, 'Activity sysId pills are not allowed in this context')
            return undefined
        }
        if (format === 'condition') {
            diagnostics.error(refShape, 'Activity sysId is only available in standard pill format, not in conditions')
            return undefined
        }
    } else if (pillPath === 'state') {
        if (!allowedSources.previousActivityOutputs) {
            diagnostics.error(refShape, 'Activity state pills are not allowed in this context')
            return undefined
        }
    } else if (pillPath.startsWith('outputs')) {
        if (!allowedSources.previousActivityOutputs) {
            diagnostics.error(refShape, 'Activity output pills are not allowed in this context')
            return undefined
        }
    }

    // Map sysId to the platform path outputs.sys_id
    const effectivePillPath = pillPath === 'sysId' ? 'outputs.sys_id' : pillPath

    const ref = await factory.createReference({
        source: refShape,
        table: ACTIVITY_TABLE,
        guid: explicitId,
    })

    return new DeferredPillShape({
        source: refShape.getSource(),
        ref,
        pillPath: effectivePillPath,
        isCondition: format === 'condition',
        isTableName,
    })
}

// ============================================================================
// Main resolver entry point
// ============================================================================

/**
 * Resolve a single Shape value from an activity input, experience property,
 * conditionToRun, or timer field. If the shape contains pill references
 * (wfa.playbook.dataPill() calls), they are resolved to platform pill strings.
 *
 * Returns the original shape unchanged for non-pill values.
 */
export async function resolvePlaybookPill(shape: Shape, context: PillContext): Promise<Shape> {
    if (isDataPillCall(shape)) {
        return resolveDataPillCall(shape, context)
    }
    if (shape instanceof TemplateExpressionShape) {
        return resolveTemplatePill(shape, context)
    }
    return shape
}

async function resolveDataPillCall(call: CallExpressionShape, context: PillContext): Promise<Shape> {
    const refShape = extractPillReference(call)
    if (!refShape) {
        context.diagnostics.error(call, 'wfa.playbook.dataPill() requires a reference argument')
        return call
    }

    // PropertyAccessShape — the common case
    if (refShape instanceof PropertyAccessShape) {
        const allElements = refShape.getElements()
        const lastElementName = allElements[allElements.length - 1]?.getName()
        const isTableName = lastElementName === '_tableName'

        // Effective elements: strip trailing _tableName if present
        const elements = isTableName ? allElements.slice(0, -1) : allElements

        // Condition-format guard for _tableName pills
        if (isTableName && context.format === 'condition') {
            context.diagnostics.error(call, 'Table name pills are not supported in conditions')
            return call
        }

        const rootName = elements[0]?.getName() ?? ''

        const effectiveCode = elements.map((el) => el.getName()).join('.')
        if (effectiveCode.startsWith(CURRENT_ACTIVITY_CALLEE + '.')) {
            const property = effectiveCode.slice(CURRENT_ACTIVITY_CALLEE.length + 1)
            // wfa.playbook.currentActivity.label / .description
            if (property === 'label' || property === 'description') {
                if (!context.allowedSources.currentActivityLabelDesc) {
                    context.diagnostics.error(
                        call,
                        'wfa.playbook.currentActivity can only be used in activity inputs or experience properties'
                    )
                    return call
                }
                if (!context.currentActivityId) {
                    context.diagnostics.error(call, 'wfa.playbook.currentActivity is not available in this context')
                    return call
                }
                return new DeferredPillShape({
                    source: call.getSource(),
                    ref: context.currentActivityId,
                    pillPath: property,
                    isCondition: context.format === 'condition',
                })
            }
        }

        // Trigger parameter: <param>.current or <param>.current.field
        if (context.triggerParamName && rootName === context.triggerParamName) {
            const second = elements[1]?.getName()
            if (second === 'current') {
                if (elements.length === 2) {
                    // trigger.current → {{triggerRecord}}
                    return new StringLiteralShape({
                        source: call.getSource(),
                        literalText: `{{triggerRecord${tnSuffix(isTableName)}}}`,
                    })
                }
                if (elements.length >= 3) {
                    // trigger.current.field or trigger.current.field.subfield → {{triggerRecord.field.subfield}}
                    const path = elements
                        .slice(2)
                        .map((el) => el.getName())
                        .join('.')
                    return new StringLiteralShape({
                        source: call.getSource(),
                        literalText: `{{triggerRecord.${path}${tnSuffix(isTableName)}}}`,
                    })
                }
            }
            context.diagnostics.error(
                call,
                'Only trigger.current and its fields are supported in trigger data pills. Use trigger.current or trigger.current.field'
            )
            return call
        }

        // PlaybookBodyParams parameter: <param>.inputs.* or <param>.state or <param>.parentRecord
        if (context.playbookBodyParamsNames.includes(rootName)) {
            const resolved = resolveParamsPill(
                elements,
                context.format,
                call,
                context.diagnostics,
                context.allowedSources,
                isTableName
            )
            if (resolved !== undefined) {
                return new StringLiteralShape({ source: call.getSource(), literalText: resolved })
            }
            // Only emit "Unsupported property" when the second element is unknown
            // (known properties emit their own error inside resolveParamsPill)
            const secondName = elements[1]?.getName()
            const isKnownParamsProperty =
                secondName === 'inputs' || secondName === 'state' || secondName === 'parentRecord'
            if (!isKnownParamsProperty) {
                context.diagnostics.error(
                    call,
                    `Unsupported property "${secondName}" on PlaybookBodyParams in data pill`
                )
            }
            return call
        }

        // Activity variable reference (same-lane or cross-lane via closure scope)
        const activityPill = await resolveActivityPill(
            refShape,
            elements,
            context.format,
            context.factory,
            context.diagnostics,
            context.allowedSources,
            isTableName
        )
        if (activityPill) {
            return activityPill
        }

        context.diagnostics.error(call, `Cannot resolve data pill reference: "${refShape.getCode()}"`)
        return call
    }

    if (refShape instanceof IdentifierShape) {
        context.diagnostics.error(
            call,
            `Data pill reference must be a property access (e.g., params.inputs.field), got identifier "${refShape.getName()}"`
        )
        return call
    }

    if (refShape instanceof StringLiteralShape) {
        // Special case for string literals, used for automation plan pill like
        // wfa.playbook.dataPill('{{vl.Label.<id>./start.input1}}')
        if (!context.allowedSources.automationPlan) {
            context.diagnostics.error(call, 'Literal string data pill can only be used in experience properties')
            return call
        }
        if (!refShape.getValue().startsWith('{{vl.')) {
            context.diagnostics.error(call, 'Literal string data pill must reference an automation plan')
            return call
        }
        return refShape
    }

    context.diagnostics.error(
        call,
        `Unsupported data pill reference expression (shape: ${refShape?.constructor?.name})`
    )
    return call
}

/**
 * Resolve a TemplateExpressionShape that may contain embedded dataPill() calls.
 *
 * A template literal like:
 *   `Hello ${dataPill(params.inputs.name)}, ticket ${dataPill(act1.outputs.id)} is ready`
 *
 * is represented in the shape system as a TemplateExpressionShape with:
 *   - literalText: "Hello "              (the head text before the first ${})
 *   - spans[0]:
 *       expression: dataPill(params.inputs.name)
 *       literalText: ", ticket "          (trailing text after this expression)
 *   - spans[1]:
 *       expression: dataPill(act1.outputs.id)
 *       literalText: " is ready"          (trailing text after this expression)
 *
 * This function walks the head + spans in order, resolving each expression
 * through resolvePlaybookPill. If any expression resolves to a DeferredPillShape
 * (i.e. it references an activity whose GUID isn't known yet), the whole template
 * becomes a DeferredTemplatePillShape so the final string is assembled lazily at
 * XML serialization time.
 *
 * If all expressions resolve to concrete strings, the parts are joined immediately
 * into a single StringLiteralShape.
 *
 * NOTE: TemplateExpressionShape has a built-in getValue(substitutions) that accepts
 * a synchronous callback, but pill resolution is async, so we walk the spans manually.
 */
async function resolveTemplatePill(template: TemplateExpressionShape, context: PillContext): Promise<Shape> {
    const spans = template.getSpans()
    if (spans.length === 0) {
        return template
    }

    const parts: Array<string | DeferredPillShape | DeferredTemplatePillShape> = []
    let hasDeferred = false

    // Start with the head literal text (everything before the first ${})
    const headText = template.getLiteralText()
    if (headText) {
        parts.push(headText)
    }

    for (const span of spans) {
        // Resolve the expression inside this ${} — may return a DeferredPillShape,
        // a concrete StringLiteralShape, or the original shape if it's not a pill
        const resolvedExpr = await resolvePlaybookPill(span.getExpression(), context)

        if (resolvedExpr instanceof DeferredPillShape) {
            parts.push(resolvedExpr)
            hasDeferred = true
        } else if (resolvedExpr instanceof DeferredTemplatePillShape) {
            // Nested template with deferred parts — shouldn't happen in practice
            // but handled for completeness
            parts.push(resolvedExpr)
            hasDeferred = true
        } else {
            parts.push(resolvedExpr instanceof StringShape ? resolvedExpr.getValue() : String(resolvedExpr.getValue()))
        }

        // Append the trailing literal text that follows this ${} expression
        // (up to the next ${} or end of template)
        const trailingText = span.getLiteralText()
        if (trailingText) {
            parts.push(trailingText)
        }
    }

    // If any part is deferred, wrap everything in a DeferredTemplatePillShape
    // so the final string is assembled lazily when getValue() is called
    if (hasDeferred) {
        return new DeferredTemplatePillShape({ source: template.getSource(), parts })
    }

    // All parts are concrete — collapse into a single string
    const fullString = parts.join('')
    return new StringLiteralShape({ source: template.getSource(), literalText: fullString })
}
